/**
 * React Query Client Configuration
 *
 * Centralized query client with optimized defaults for the application.
 * Includes intelligent error handling and retry logic.
 */

import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { ApiError } from "@/lib/api/client";
import { emitSessionExpired } from "@/lib/auth-events";
import { FormValidationError } from "@/lib/custom-errors";

/**
 * Determine if an error should be retried
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  // Don't retry more than 2 times
  if (failureCount >= 2) return false;

  // If it's our ApiError, use its retry logic
  if (error instanceof ApiError) {
    // Don't retry auth errors - user needs to re-authenticate
    if (error.isAuthError) return false;
    // Don't retry validation errors - same request will fail again
    if (error.isValidationError) return false;
    // Don't retry not found - resource doesn't exist
    if (error.isNotFound) return false;
    // Retry network errors and server errors
    return error.isRetryable;
  }

  // Default: retry once for unknown errors
  return failureCount < 1;
}

/**
 * Global error handler for queries
 */
export function handleQueryError(error: unknown) {
  // Log all query errors in development
  if (process.env.NODE_ENV === "development") {
    console.error("[QueryCache] Query error:", error);
  }

  // Handle authentication errors globally - trigger session expiry
  if (error instanceof ApiError && error.isAuthError) {
    console.warn(
      "[QueryCache] Authentication error - triggering session expiry",
    );
    emitSessionExpired(error.message, error.status);
  }
}

/**
 * Global error handler for mutations
 */
export function handleMutationError(error: unknown) {
  // FormValidationError is an expected outcome of business-rule checks
  // inside mutationFn (e.g. "completionNotes required to mark an action
  // complete"). The per-mutation onError still fires — toast + cache
  // rollback both run via the consumer's hooks. Downgrade to console.debug
  // so the global logger doesn't drown the dev console with expected
  // rejections, but the line stays accessible to anyone debugging.
  if (error instanceof FormValidationError) {
    if (process.env.NODE_ENV === "development") {
      console.debug(
        "[MutationCache] FormValidationError (handled by mutation):",
        error,
      );
    }
    return;
  }

  // Log all other mutation errors
  console.error("[MutationCache] Mutation error:", error);

  // Handle authentication errors globally - trigger session expiry
  if (error instanceof ApiError && error.isAuthError) {
    console.warn(
      "[MutationCache] Authentication error - triggering session expiry",
    );
    emitSessionExpired(error.message, error.status);
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
  defaultOptions: {
    queries: {
      // Data considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Smart retry based on error type
      retry: shouldRetry,

      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Don't refetch on window focus (can be noisy)
      refetchOnWindowFocus: false,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Throw errors to error boundaries in Suspense mode
      throwOnError: (error) => {
        // Only throw to error boundary for severe errors
        return error instanceof ApiError && error.status >= 500;
      },
    },
    mutations: {
      // Smart retry for mutations
      retry: (failureCount, error) => {
        // Be more conservative with mutations - only retry network errors
        if (failureCount >= 1) return false;
        if (error instanceof ApiError) {
          return error.isNetworkError || error.isTimeout;
        }
        return false;
      },

      // Exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  },
});

/**
 * Invalidate all queries - use sparingly (e.g., after logout)
 */
export function invalidateAllQueries() {
  return queryClient.invalidateQueries();
}

/**
 * Clear all cache - use for logout or user switch
 */
export function clearQueryCache() {
  return queryClient.clear();
}

/**
 * Prefetch a query (useful for anticipated navigation)
 */
export function prefetchQuery<T>(
  queryKey: unknown[],
  queryFn: () => Promise<T>,
  staleTime?: number,
) {
  return queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: staleTime ?? 5 * 60 * 1000,
  });
}

/**
 * Get query error message
 */
export function getQueryErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}
